import { useEffect, useMemo } from 'react';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import {
  ButtonGroup,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { ApiError } from '@/api/management/requests/core/ApiError';
import { TotalSeaType, TugOperationsConfiguration, getTugOperationsConfiguration } from '@/api/management/tugOperationsService';
import { ColorPickerPopover } from '@/components/common/PopOver/ColorPickerPopover';
import ApiErrorAlert from '@/components/feedback/Error/ApiErrorAlert';
import LoadingIndicator from '@/components/feedback/LoadingIndicator';
import { tugOperationsConfigurationAtom } from '@/features/Admin/OrganisationManagement/ConfigureGraph/ConfigureGraphForOrganisation';

type TugOperationsConfigSectionProps = {
  organisationId: string;
};

function TugOperationsConfigSection({ organisationId }: TugOperationsConfigSectionProps) {
  const [rows, setRows] = useAtom(tugOperationsConfigurationAtom);

  const {
    data: configurations,
    isLoading,
    isError,
    error,
  } = useQuery<TugOperationsConfiguration[]>({
    queryKey: ['management-tug-operations-config', organisationId],
    enabled: !!organisationId,
    refetchOnWindowFocus: false,
    retry: false,
    queryFn: async () => {
      try {
        return await getTugOperationsConfiguration(organisationId);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          return [];
        }
        throw e;
      }
    },
  });

  useEffect(() => {
    setRows(configurations ?? []);
  }, [configurations, setRows]);

  const isVisible = useMemo(() => rows.length > 0, [rows]);

  const blankRow: TugOperationsConfiguration = useMemo(
    () => ({
      organisationId,
      label: null,
      abbr: null,
      description: null,
      thresholdColor: '#0d74dc',
      thresholdValue: null,
      seaType: 'HEIGHT',
    }),
    [organisationId]
  );

  const addRow = (at: number) => {
    setRows((current) => [...current.slice(0, at), { ...blankRow }, ...current.slice(at)]);
  };

  const removeRow = (at: number) => {
    setRows((current) => current.filter((_, index) => index !== at));
  };

  const updateRow = (at: number, patch: Partial<TugOperationsConfiguration>) => {
    setRows((current) => current.map((row, index) => (index === at ? { ...row, ...patch } : row)));
  };

  if (isLoading) {
    return <LoadingIndicator message={'Loading Tug operations configuration...'} />;
  }

  if (isError) {
    return <ApiErrorAlert error={error} />;
  }

  if (!isVisible) {
    return null;
  }

  return (
    <Stack minWidth={1000} maxWidth={1200} sx={{ pt: 2 }}>
      <Typography variant={'h3'}>Tug operations</Typography>

      <Table aria-label="tug operations configuration table" border={0} size={'small'}>
        <TableHead>
          <TableRow sx={{ border: 0 }}>
            <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
              <Typography variant={'h6'}>Label</Typography>
            </TableCell>
            <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
              <Typography variant={'h6'}>Abbr.</Typography>
            </TableCell>
            <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
              <Typography variant={'h6'}>Description</Typography>
            </TableCell>
            <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
              <Typography variant={'h6'}>Y-axis</Typography>
            </TableCell>
            <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
              <Typography variant={'h6'}>Threshold Color</Typography>
            </TableCell>
            <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
              <Typography variant={'h6'}>Threshold value</Typography>
            </TableCell>
            <TableCell sx={{ px: 0, border: 0 }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow key={row.id ?? index}>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <FormControl size={'small'}>
                  <TextField
                    variant={'standard'}
                    value={row.label ?? ''}
                    style={{ width: 200 }}
                    onChange={(e) => updateRow(index, { label: e.target.value })}
                  />
                </FormControl>
              </TableCell>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <FormControl size={'small'}>
                  <TextField
                    variant={'standard'}
                    value={row.abbr ?? ''}
                    style={{ width: 80 }}
                    onChange={(e) => updateRow(index, { abbr: e.target.value })}
                  />
                </FormControl>
              </TableCell>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <FormControl size={'small'}>
                  <TextField
                    variant={'standard'}
                    value={row.description ?? ''}
                    style={{ width: 280 }}
                    onChange={(e) => updateRow(index, { description: e.target.value })}
                  />
                </FormControl>
              </TableCell>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <FormControl size={'small'} sx={{ minWidth: 130 }}>
                  <Select<TotalSeaType>
                    variant={'standard'}
                    value={(row.seaType as TotalSeaType) ?? 'HEIGHT'}
                    onChange={(e) => updateRow(index, { seaType: e.target.value as TotalSeaType })}
                  >
                    <MenuItem value={'HEIGHT'}>Left (Height)</MenuItem>
                    <MenuItem value={'PERIOD'}>Right (Time)</MenuItem>
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <FormControl>
                  <ColorPickerPopover
                    color={row.thresholdColor ?? '#0d74dc'}
                    onChange={(color) => updateRow(index, { thresholdColor: color })}
                  />
                </FormControl>
              </TableCell>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <FormControl size={'small'}>
                  <TextField
                    variant={'standard'}
                    type={'number'}
                    value={row.thresholdValue ?? ''}
                    style={{ width: 120 }}
                    onChange={(e) => updateRow(index, { thresholdValue: e.target.value === '' ? null : Number(e.target.value) })}
                  />
                </FormControl>
              </TableCell>
              <TableCell sx={{ px: 0, border: 0 }} align={'left'}>
                <ButtonGroup size={'small'}>
                  <IconButton color={'primary'} onClick={() => addRow(index + 1)}>
                    <AddCircle />
                  </IconButton>
                  <IconButton color={'default'} onClick={() => removeRow(index)} disabled={rows.length <= 1}>
                    <RemoveCircle />
                  </IconButton>
                </ButtonGroup>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  );
}

export default TugOperationsConfigSection;
